import {useState, useEffect, useContext, useMemo, createContext, useRef} from 'react';

//context
import { ListContext } from '@/context/ListContext';

//components
import ContentItemPublishedStatus from  '@/components/elements/lists/ContentItemPublishedStatus';
import ContentItemAudienceCount from  '@/components/elements/lists/ContentItemAudienceCount';
import ListContentTile from  '@/components/elements/lists/ListContentTitle';
import { IconLibrary, ThriveIcon } from 'thrive-components';

//interfaces
import { PaginationSettings} from  '@/interfaces/lists';
import { NotificationStatus } from  '@/interfaces/languages';
import { getPermissionString } from  '@/helpers/Content';
import { IUseCombinedFeed, CombinedFeedSortType, modeType } from  '@/interfaces/CombinedFeed';

//api
import {getAllListItems} from '../api/lists'
import { getAllSourceLists, removeAllSourceListsFromCombinedFeed, removeSourceListFromCombinedFeed, setSourceListMode } from '@/api/combinedFeed';

//helpers
// import { getEnumFromSortTypeString } from  '@/helpers/CombinedFeed';
import { scheduledStatus } from  '@/helpers/List';
import { useTimer } from  '@/components/hooks/utility/useTimer';



export const CombinedFeedContextInitialState: any = {
    contentItems: [],
    sourceLists: [],
    savingContent: false,
    titleError: false,
    modalOpen: false,
    hasListId: false,
    alteringSourceListId: undefined,
    sortType: CombinedFeedSortType.DateModifiedNewest,
    paginationSettingsCF: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0}, 
    paginationSettingsSL: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0}, 
    setSortType: () => {},
    updateListContentItems:() => {},
    updateSourceListContent: () => {},
    formatCombinedFeedContent:()=>{},
}

export const CombinedFeedContext = createContext(CombinedFeedContextInitialState);

const CombinedFeedContextProvider = ({defaultSort, children}:IUseCombinedFeed ) => {
    const {listId, appGuid, hasEditorRights, appId, listTitle} = useContext(ListContext);
    
    let fetchCFController: AbortController | undefined;
    let fetchSLController: AbortController | undefined;
    const top = 20;
    const [feedMode, setFeedMode] = useState<modeType>();
    const [contentItems, setContentItems] = useState<any[]>([]);
    const [sourceLists, setSourceLists] = useState<any[]>([]);
    const [sortType, setSortType] = useState<CombinedFeedSortType>(CombinedFeedSortType.DateModifiedNewest);
    const [paginationSettingsCF, setPaginationSettingsCF] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
    const [paginationSettingsSL, setPaginationSettingsSL] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
    const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status?: NotificationStatus, message: string}>({show: false, status: undefined, message: ""});
    const [isLoadingCF, setIsLoadingCF] = useState<boolean>(true);
    const [isLoadingSL, setIsLoadingSL] = useState<boolean>(true);
    const [isAlteringSL, setIsAlteringSL] = useState<boolean>(true);
    const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [savingContent, setSavingContent] = useState<boolean>(false);
    const [titleError, setTitleError] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [showFeedModeSelectionPanel, setShowFeedModeSelectionPanel] = useState<boolean>(feedMode === modeType.all);
    const [hasListId, setHasListId] = useState<boolean>(false);

    /* ==================== SOME X-FRAMEWORK STUFF =================== */

    // update menu items function is a closure. It's called in jquery in lists.js
    // we need to use refs to ensure that the correct and most recent values are being used in that function
    const listIdRef = useRef<string>('')
    const appGuidRef = useRef<string>('')
    const appIdRef = useRef<string>('')
    const sortTypeRef = useRef<CombinedFeedSortType>(undefined)
    const paginationSettingsRef = useRef<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0})
    const downloadContentReportLink = (listId || listIdRef.current) ? `/a/${appGuid}/Content/DownloadPublishedContentReport?listId=${listId || listIdRef.current}` : null;
    
    // whenever appguid, listid or sorttype change in context update the refs
    useEffect(() => {
        appGuidRef.current = appGuid
        appIdRef.current = appId
        listIdRef.current = listId;
        sortTypeRef.current = sortType;
        paginationSettingsRef.current = paginationSettingsCF

    }, [appGuid, listId, sortType, appId, paginationSettingsCF])

    /* ====================== END OF X-FRAMEWORK STUFF ===================== */ 

    useEffect(() => {
        setHasListId(+listId > 0)
    }, [listId]);

    useEffect(() => {
        if(+listId > 0 && (sortType as number) >= 0) {
            updateListContentItems()
        }
    }, [listId, sortType]);

    useEffect(() => {
        (window as any).functions = {...(window as any).functions, refreshMainMenu: () => updateListContentItems(paginationSettingsCF.currentPage)}
        return () => {delete (window as any)?.functions?.refreshMainMenu}
    },[appId, listId, top, sortType, paginationSettingsCF])
    
    useEffect(() => {
        setTitleError(false);
    },[listTitle])

    useEffect(() => {
        if (+listId > 0 && savingContent) {
            setModalOpen(true);
            setSavingContent(false)
        }
    },
    [listId]);

    useEffect(() => {
        if (document.querySelector('.tab-nav')) {(document.querySelector('.tab-nav') as HTMLDivElement).style.marginBottom = '24px';}

        if (!hasEditorRights) {
            if (document.querySelector('.savePanel')) {(document.querySelector('.savePanel') as HTMLDivElement).style.display = 'none';}
            if (document.querySelector('#react-schedulepush-container')) { (document.querySelector('#react-schedulepush-container') as HTMLDivElement).style.display = 'none';}
        } else {
            if (document.querySelector('.savePanel')) {(document.querySelector('.savePanel') as HTMLDivElement).style.display = 'block';}
            if (document.querySelector('#react-schedulepush-container')) { (document.querySelector('#react-schedulepush-container') as HTMLDivElement).style.display = 'block';}
        }
    }, [hasEditorRights])

    useTimer(notificationDetails?.show ?? false, ()=>setNotificationDetails({show: false, status:undefined, message: ""}), 3000);

    function updateListContentItems(page?: number){
        if (!listIdRef.current && !listId){ return}
        if (fetchCFController) { fetchCFController.abort() }
        fetchCFController = new AbortController();

        let skip = 0;
        if (page) { skip = ((page - 1) * top);} 

        getAllListItems(appIdRef.current || appId, listIdRef.current || listId, top, skip, false, sortType, fetchCFController.signal)
        .then((data) =>   {
            setContentItems(data.records);

            if (sortType === undefined) {
                setSortType(data.SortType)
            }
            setPaginationSettingsCF({
                hasPagination: data.total > top,
                currentPage:  Math.ceil(page ?? 1),
                totalPages: Math.ceil(data.total / top),
                pageSize: top,
                totalItems: data.total
            })
        })
        .catch((error: any) => {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message: error.response?.data
            });
        })
        .finally(() => {
            setIsLoadingCF(false);
            updateSourceListContent();
        })
    }

    function updateSourceListContent(page?: number){
        if (!listIdRef.current && !listId){ return}
        if (fetchSLController) { fetchSLController.abort() }
        fetchSLController = new AbortController();

        let skip = 0;
        if (page) { skip = ((page - 1) * top);} 

        getAllSourceLists(listIdRef.current || listId, top, skip, fetchSLController.signal)
        .then((data) =>   {
            setFeedMode(data.modeType);
            setSourceLists(data.records);
            setPaginationSettingsSL({
                hasPagination: data.total > top,
                currentPage:  Math.ceil(page ?? 1),
                totalPages: Math.ceil(data.total / top),
                pageSize: top,
                totalItems: data.total
            })
        })
        .catch((error: any) => {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message: error.response?.data
            });
        })
        .finally(() => {
            setIsLoadingSL(false);
            setIsAlteringSL(false);
        })
    }

    function formatCombinedFeedContent(contentItems: any[], isSourceListFeed: boolean){
        if (!contentItems){
            return
        }
        return contentItems.map((item) => ({
            content: [
                <ListContentTile 
                    key={`${item.id}-content-title`}
                    listId={listId}
                    appGuid={appGuid}
                    item={item}
                    status={<ContentItemPublishedStatus item={item}/>}
                    archiveStatus={scheduledStatus(item.scheduledPublishDate, item.scheduledArchiveDate)}
                />, 
                <ContentItemAudienceCount
                    key={`${item.id}-audience-count`}
                    count={item.groupUserCount}
                    everyoneHasAccess={item.everyoneHasAccess}
                />,
                getPermissionString(item.permission)
            ],
            controls: 
                isSourceListFeed 
                ?   [
                        {
                            content: <span className='remove-list-button'><ThriveIcon iconName={IconLibrary.DASH_CIRCLE} color='var(--thriveRed)' sizeInPx='12' /> Remove List</span>,
                            onClick: () => removeSourceList(item.id),
                            isDisabled: false,
                            isSelected: false
                        }
                    ]
                : [],
        }))
    }

    function setSourceListModeType(modeType: modeType){
        setModalIsLoading(true);
        setIsLoadingSL(true);

        setSourceListMode(listIdRef.current || listId, {mode: modeType})
        .catch((error: any) => {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message: error.response?.data
            });
        })
        .finally(() =>   {
            updateListContentItems();
            setModalIsLoading(false);
            setShowFeedModeSelectionPanel(modeType === "all");
        })
    }

    function removeSourceList(sourceListId: number){
        setIsAlteringSL(true);
        setShowConfirmationModal(false);
        removeSourceListFromCombinedFeed(listIdRef.current || listId, sourceListId)
        .then(() =>   {
            updateListContentItems();
        })
        .catch((error: any) => {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message: error.response?.data
            });
        })
    }

    function onSourceListReset(){
        setIsLoadingSL(true);

        removeAllSourceListsFromCombinedFeed(listIdRef.current || listId)
        .then(() =>   {
            updateListContentItems();
        })
        .catch(() => {
            setNotificationDetails({show: true, status: NotificationStatus.error, message: "Something went wrong with removing all source lists. Please try again."});
        })
        .finally(() => {
            setShowConfirmationModal(false);
        })
    }

    async function handleModalOpen(isOpen: boolean) {
        setShowFeedModeSelectionPanel(feedMode === "all" || sourceLists?.length < 1);
        const titleInput = document.querySelector('#Title') as HTMLInputElement;
        if (!titleInput.value) {
            setTitleError(true)
            return 
        } else if (+listId === 0) {
            setTitleError(false);

            if(!savingContent) {
                await (window as any)?.SaveContent(false);
                setSavingContent(true);
            }   
        } else {
            setModalOpen(isOpen);
        }
    }

    function handleModalClose() {
        setIsLoadingCF(true);
        setIsLoadingSL(true);
        setModalOpen(false);
        updateListContentItems();
    }

    const context = useMemo(() => ({
        contentItems,
        sourceLists,
        sortType,
        paginationSettingsCF,
        paginationSettingsSL,
        notificationDetails,
        modalOpen, 
        hasEditorRights,
        isLoadingCF,
        isLoadingSL,
        downloadContentReportLink,
        savingContent, 
        titleError, 
        hasListId,
        feedMode,
        modalIsLoading,
        showFeedModeSelectionPanel,
        isAlteringSL,
        showConfirmationModal,
        setShowConfirmationModal,
        setModalIsLoading,
        setNotificationDetails,
        setFeedMode,
        onSourceListReset,
        removeSourceList,
        setSourceListModeType,
        formatCombinedFeedContent,
        setSortType,
        updateListContentItems,
        updateSourceListContent,
        handleModalOpen,
        handleModalClose
    }), [contentItems, modalIsLoading, showFeedModeSelectionPanel, showConfirmationModal, sourceLists, modalIsLoading, sortType, paginationSettingsCF, paginationSettingsSL, notificationDetails, hasEditorRights, isLoadingSL, isLoadingCF, titleError, modalOpen, hasListId, feedMode, savingContent, isAlteringSL])

    return (
        <CombinedFeedContext.Provider value={context}>
            {children}
        </CombinedFeedContext.Provider>
    )
}

export default CombinedFeedContextProvider